export const LAYOUT_FIELDS = [
  "code",
  "subtitle",
  "docType",
  "client",
  "date",
  "version",
  "classification",
  "tagline",
  "logo.cover",
  "logo.header",
  "title",
] as const;
export type LayoutField = (typeof LAYOUT_FIELDS)[number];
export type LayoutFields = Record<LayoutField, string>;

const KNOWN: ReadonlySet<string> = new Set(LAYOUT_FIELDS);

export class TemplateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TemplateError";
  }
}

export type TemplateNode =
  | { kind: "text"; text: string }
  | { kind: "field"; name: LayoutField }
  | { kind: "section"; name: LayoutField; children: TemplateNode[] };

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Parses the mustache subset: {{field}} and {{#field}}…{{/field}}. Throws TemplateError on anything else. */
export function parseTemplate(tpl: string): TemplateNode[] {
  const root: TemplateNode[] = [];
  const stack: Array<{ name: LayoutField; children: TemplateNode[] }> = [];
  const current = (): TemplateNode[] => stack.at(-1)?.children ?? root;
  let i = 0;
  while (i < tpl.length) {
    const open = tpl.indexOf("{{", i);
    if (open === -1) {
      current().push({ kind: "text", text: tpl.slice(i) });
      break;
    }
    if (open > i) current().push({ kind: "text", text: tpl.slice(i, open) });
    const close = tpl.indexOf("}}", open + 2);
    if (close === -1) throw new TemplateError(`unclosed tag at offset ${open}`);
    const inner = tpl.slice(open + 2, close).trim();
    const m = /^([#/]?)\s*([A-Za-z][A-Za-z.]*)$/.exec(inner);
    if (!m) throw new TemplateError(`malformed tag {{${inner}}}`);
    const sigil = m[1] ?? "";
    const name = m[2] ?? "";
    if (!KNOWN.has(name)) throw new TemplateError(`unknown field {{${name}}}`);
    const field = name as LayoutField;
    if (sigil === "#") {
      stack.push({ name: field, children: [] });
    } else if (sigil === "/") {
      const top = stack.pop();
      if (!top || top.name !== field) throw new TemplateError(`unexpected {{/${name}}}`);
      current().push({ kind: "section", name: field, children: top.children });
    } else {
      current().push({ kind: "field", name: field });
    }
    i = close + 2;
  }
  const open = stack.at(-1);
  if (open) throw new TemplateError(`unclosed section {{#${open.name}}}`);
  return root;
}

function renderNodes(nodes: TemplateNode[], fields: Partial<LayoutFields>): string {
  return nodes
    .map((n) => {
      if (n.kind === "text") return n.text;
      const value = fields[n.name] ?? "";
      if (n.kind === "field") return escapeHtml(value);
      return value !== "" ? renderNodes(n.children, fields) : "";
    })
    .join("");
}

export function renderTemplate(tpl: string, fields: Partial<LayoutFields>): string {
  return renderNodes(parseTemplate(tpl), fields);
}
